import React, { Component, Fragment } from 'react';
import { connect } from "react-redux";
import { IRootState } from "../../../store";
import DispatchProps from "../../../utils/DispatchProps";
import ApiService from "../../ApiService";
import {
    Client as GraphClient,
} from "@microsoft/microsoft-graph-client";
import * as H from "history";
import * as _ from 'lodash';
import UserAccessRecord, { UserAccessLevel, userAccessLevelIsAtLeast } from '../../../models/UserAccessRecord';
import Table, { TableColumn } from '../../Table';
import PageTitle from '../../PageTitle';
import { ActionButton, CommandBarButton, DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from 'office-ui-fabric-react';
import TableCell from '../../TableCell';
import { Link } from 'react-router-dom';
import { AccountInfo } from '@azure/msal-browser';
import {AuthActions} from "../../../redux/reducers/Auth";
import {Dictionary} from "lodash";

interface GraphUser {
    displayName: string;
    userPrincipalName: string;
}

interface ListUserAccessRecordsPageProps {
    history: H.History,
    user?: AccountInfo,
    usersCdbAccessLevel?: UserAccessLevel,
    graphClient: GraphClient;
    apiService: ApiService;
}

interface ListUserAccessRecordsPageState {
    showDeleteDialog: boolean;
    accessRecordsWithUsers: { accessRecord: UserAccessRecord, user?: GraphUser }[];
    accessRecordToDelete?: { accessRecord: UserAccessRecord, user?: GraphUser };
}

class ListUserAccessRecordsPage extends Component<ListUserAccessRecordsPageProps & DispatchProps, ListUserAccessRecordsPageState> {
    static displayName = ListUserAccessRecordsPage.name;

    public static async getUserDict(userOids: string[], graphClient: GraphClient) : Promise<Dictionary<GraphUser>>
    {
        const userDict: { [key: string]: GraphUser } = {};

        const userOidChunks = _.chunk(userOids, 15);

        for (const userOidChunk of userOidChunks) {
            const filterConditions = userOidChunk.map(userOid => `id eq '${userOid}'`);
            const filter = _.join(filterConditions, ' or ');
            const result: any = await graphClient.api("/users").filter(filter).get();
            const users = _.keyBy(result.value, "id");

            Object.assign(userDict, users);
        }
        
        return userDict;
    }
    
    private static columns: TableColumn[] = [
        {
            title: "User",
            propertyPath: "userOid",
            flex: 1
        },
        {
            title: "Level",
            propertyPath: "level",
            flex: 1
        },
        {
            title: "",
            propertyPath: "delete",
            flex: 1
        }
    ];

    constructor(props: ListUserAccessRecordsPageProps & DispatchProps) {
        super(props);

        this.state = {
            showDeleteDialog: false,
            accessRecordsWithUsers: [],
            accessRecordToDelete: undefined
        }
    }

    private async fetchAccessRecords() {
        const accessRecords = await this.props.apiService.getUserAccessRecords();

        const userOids = accessRecords.map(ar => ar.userOid!);
        const userDict = await ListUserAccessRecordsPage.getUserDict(userOids,this.props.graphClient);

        const accessRecordsWithUsers = accessRecords.map(accessRecord => {
            return {
                accessRecord,
                user: userDict[accessRecord.userOid!]
            };
        })

        this.setState({
            accessRecordsWithUsers: _.sortBy(accessRecordsWithUsers, "user.displayName")
        });
    }

    async componentDidMount() {
        await this.fetchAccessRecords();
    }

    async componentDidUpdate(prevProps: Readonly<ListUserAccessRecordsPageProps & DispatchProps>, prevState: any): Promise<void> {

    }

    async deleteUserAccessRecord() {
        if (this.state.accessRecordToDelete) {
            await this.props.apiService.deleteUserAccessRecord(this.state.accessRecordToDelete!.accessRecord);

            const deletingSelf = this.props.user?.localAccountId === this.state.accessRecordToDelete.accessRecord.userOid;

            this.setState({
                showDeleteDialog: false,
                accessRecordToDelete: undefined
            });

            if (deletingSelf) {
                this.props.dispatch(AuthActions.getCdbUserAccessLevel(true));
            }

            await this.fetchAccessRecords();
        }
    }

    private canAddAndDelete() {
        return userAccessLevelIsAtLeast("admin", this.props.usersCdbAccessLevel);
    }

    private cellForColumn = (item: { accessRecord: UserAccessRecord, user?: GraphUser }, column: TableColumn, index: number) => {

        let content = null;
        if (column.propertyPath === "userOid") {
            if (item.user) {
                content = (
                    <Fragment>
                        <span>{item.user.displayName}</span>
                        <span className={"cdb-table__extra-detail"}>{item.user.userPrincipalName}</span>
                    </Fragment>
                );
            }
            else {
                content = (<span>{item.accessRecord.userOid}</span>);
            }
        }
        else if (column.propertyPath === "delete") {
            content = (
                <ActionButton iconProps={{ iconName: "Delete" }} allowDisabledFocus disabled={!this.canAddAndDelete()} checked={false} onClick={() => {
                    this.setState({
                        showDeleteDialog: true,
                        accessRecordToDelete: item
                    });
                }}>
                    Remove
                </ActionButton>
            );
        }
        else {
            content = item.accessRecord[column.propertyPath];
        }

        return (
            <TableCell key={index} flex={column.flex}>
                {content}
            </TableCell>
        );
    };

    render() {
        return (
            <Fragment>
                <PageTitle title={`CDB User Access`} />

                <Table
                    title={"User Access"}
                    items={this.state.accessRecordsWithUsers}
                    columns={ListUserAccessRecordsPage.columns}
                    cellForColumn={this.cellForColumn}>

                    {this.canAddAndDelete() &&
                        <Link to={`/cdb-admin/user-access/add`} >
                            <CommandBarButton
                                iconProps={{ iconName: "add" }}
                                text="Add"
                                onClick={() => { }}
                            />
                        </Link>
                    }
                </Table>

                <Dialog
                    hidden={!this.state.showDeleteDialog}
                    onDismiss={() => {
                        this.setState({
                            showDeleteDialog: false
                        });
                    }}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: 'Remove Access',
                        subText: `Do you want to remove access for ${this.state.accessRecordToDelete?.user?.userPrincipalName}?`
                    }}
                    modalProps={{
                        isBlocking: false,
                        styles: { main: { maxWidth: 900 } },
                    }}>
                    <DialogFooter>
                        <PrimaryButton onClick={async () => {
                            await this.deleteUserAccessRecord();
                            this.setState({
                                showDeleteDialog: false,
                            });
                        }} text="Remove" />
                        <DefaultButton onClick={() => {
                            this.setState({
                                showDeleteDialog: false,
                                accessRecordToDelete: undefined,
                            });
                        }} text="Cancel" />
                    </DialogFooter>
                </Dialog>

            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { history: H.History, match: any }): ListUserAccessRecordsPageProps => {
        return {
            history: ownProps.history,
            user: state.auth.user!,
            usersCdbAccessLevel: state.auth.usersCdbAccessLevel,
            graphClient: state.auth.graphClient,
            apiService: state.auth.apiService,
        };
    }
)(ListUserAccessRecordsPage)